import type { QueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../api/client';
import { listExpenses } from '../../../api/endpoints/expenses';
import { getPnl } from '../../../api/endpoints/pnl';
import { getBusinessReport } from '../../../api/endpoints/reports';
import type { BusinessReport, ExpenseRecord, PnlResult } from '../../../api/schemas';
import { periodFor, type ReportPeriod } from '../../../lib/reports';

/**
 * What the team knows about the business's money when the owner asks. The
 * numbers come from the same endpoints, and the same cache, as Finanzas and
 * Reportes, so an answer can never disagree with the screen next to it.
 */

/** A window of days the owner can ask about. */
export type ReportKey = 'hoy' | 'ayer' | 'semana' | 'mes' | 'mes-pasado' | 'mismos-dias' | 'anio';
/** `gastos` is the expense breakdown of the month and the list of recurring expenses. */
export type FinanceNeed = ReportKey | 'gastos';

export type FinanceReady = {
  status: 'ready';
  now: Date;
  reports: Partial<Record<ReportKey, BusinessReport>>;
  pnl?: PnlResult;
  expenses?: ExpenseRecord[];
};
export type FinanceFacts = { status: 'locked' } | { status: 'error' } | FinanceReady;

const REPORT_KEYS: readonly ReportKey[] = ['hoy', 'ayer', 'semana', 'mes', 'mes-pasado', 'mismos-dias', 'anio'];
const FRESH_MS = 60_000;
const FORBIDDEN = 403;
const DAY_MS = 86_400_000;

/** Panamá's calendar: today, yesterday, the last seven days, the month so far, last month, the same days of last month, the year so far. */
export function periodsFor(now: Date): Record<ReportKey, ReportPeriod> {
  const hoy = periodFor('hoy', now);
  const lastMonth = periodFor('mes-pasado', now);
  const day = Math.min(Number(hoy.from.slice(8)), Number(lastMonth.to.slice(8)));
  const yesterday = periodFor('hoy', new Date(now.getTime() - DAY_MS)).from;
  return {
    hoy,
    ayer: { from: yesterday, to: yesterday },
    semana: periodFor('7dias', now),
    mes: periodFor('mes', now),
    'mes-pasado': lastMonth,
    'mismos-dias': { from: lastMonth.from, to: `${lastMonth.to.slice(0, 8)}${String(day).padStart(2, '0')}` },
    anio: periodFor('anio', now),
  };
}

export async function loadFinanceFacts(
  client: QueryClient,
  needs: readonly FinanceNeed[],
  now: Date,
): Promise<FinanceFacts> {
  const wanted = new Set(needs);
  const periods = periodsFor(now);
  const keys = REPORT_KEYS.filter((key) => wanted.has(key));
  try {
    const [loaded, pnl, expenses] = await Promise.all([
      Promise.all(keys.map((key) => fetchReport(client, periods[key]))),
      wanted.has('gastos') ? fetchPnl(client, periods.mes) : undefined,
      wanted.has('gastos') ? fetchExpenses(client) : undefined,
    ]);
    const reports = Object.fromEntries(keys.map((key, index) => [key, loaded[index]])) as FinanceReady['reports'];
    return { status: 'ready', now, reports, ...(pnl ? { pnl } : {}), ...(expenses ? { expenses } : {}) };
  } catch (error) {
    return error instanceof ApiError && error.status === FORBIDDEN ? { status: 'locked' } : { status: 'error' };
  }
}

function fetchReport(client: QueryClient, period: ReportPeriod): Promise<BusinessReport> {
  return client.fetchQuery({
    queryKey: ['business-report', period.from, period.to],
    queryFn: () => getBusinessReport(period),
    staleTime: FRESH_MS,
  });
}

function fetchPnl(client: QueryClient, period: ReportPeriod): Promise<PnlResult> {
  return client.fetchQuery({
    queryKey: ['business-pnl', period.from, period.to],
    queryFn: () => getPnl(period.from, period.to),
    staleTime: FRESH_MS,
  });
}

async function fetchExpenses(client: QueryClient): Promise<ExpenseRecord[]> {
  const response = await client.fetchQuery({
    queryKey: ['business-expenses'],
    queryFn: listExpenses,
    staleTime: FRESH_MS,
  });
  return response.data;
}
